<template>
  <div>
    <div class="wz-page-header">
      <h1 class="wz-page-title">{{ editing ? "Editar tipo de documento" : "Nuevo tipo de documento" }}</h1>
    </div>

    <form @submit.prevent="save" novalidate>
      <wz-card :title="editing ? 'Datos del tipo de documento' : 'Datos del nuevo tipo de documento'">
        <div class="flex flex-wrap -mx-2">
          <div v-if="editing" class="w-full md:w-1/3 mb-3 px-2">
            <wz-input
              label="Código"
              :disabled="true"
              v-model="form.code"
            />
          </div>
          <div class="w-full md:w-2/3 mb-3 px-2">
            <wz-input
              label="Nombre"
              required
              v-model="form.name"
              placeholder="Nombre del tipo de documento"
              :error="$v.form.name.$error && !$v.form.name.required ? 'El nombre es requerido' : ''"
            />
          </div>
          <div class="w-full mb-3 px-2">
            <wz-textarea label="Descripción" v-model="form.description" placeholder="Descripción del tipo de documento" :rows="3" />
          </div>

          <!-- Bloque de obligatoriedad -->
          <div class="w-full mb-3 px-2">
            <div class="mb-2">
              <wz-checkbox v-model="isObligatory" switch>
                <strong>Este documento es obligatorio</strong>
              </wz-checkbox>
            </div>

            <div v-if="isObligatory" class="mt-2">
              <!-- Radio: Para todos / Específicos -->
              <div class="flex gap-4 mb-3">
                <label class="flex items-center gap-2 cursor-pointer">
                  <input type="radio" v-model="form.requirementScope" value="ALL" />
                  <span>Para todos los colaboradores</span>
                </label>
                <label class="flex items-center gap-2 cursor-pointer">
                  <input type="radio" v-model="form.requirementScope" value="SPECIFIC" />
                  <span>Solo para empresas / cargos específicos</span>
                </label>
              </div>

              <!-- Requisitos por par empresa/cargo (semántica AND; null = cualquiera) -->
              <div v-if="form.requirementScope === 'SPECIFIC'" class="mb-2">
                <label class="wz-form-label">Requisitos por empresa y/o cargo</label>
                <p class="text-muted text-sm mb-2">
                  Cada fila es un requisito. Si eliges empresa <strong>y</strong> cargo, aplica solo a quien cumple
                  ambos. Deja "Cualquier empresa" o "Cualquier cargo" para no condicionar ese lado.
                  Debes elegir al menos uno por fila.
                </p>

                <div
                  v-for="(req, idx) in requirements"
                  :key="idx"
                  class="flex flex-wrap items-end gap-2 mb-2 pb-2"
                  style="border-bottom:1px solid var(--admin-border-color)"
                >
                  <div class="flex-1" style="min-width:200px">
                    <label class="wz-form-label">Empresa</label>
                    <wz-select
                      :items="companyOptions"
                      v-model="req.companyId"
                      defaultOption="Cualquier empresa"
                    />
                  </div>
                  <div class="flex-1" style="min-width:200px">
                    <label class="wz-form-label">Cargo</label>
                    <wz-select
                      :items="positionOptions"
                      v-model="req.positionId"
                      defaultOption="Cualquier cargo"
                    />
                  </div>
                  <div class="mb-1">
                    <wz-button variant="danger" @click="removeRequirement(idx)">
                      <i class="ri-delete-bin-line"></i>
                    </wz-button>
                  </div>
                </div>

                <wz-button variant="light" @click="addRequirement">
                  <i class="ri-add-line mr-1"></i>Agregar requisito
                </wz-button>
                <p v-if="requirementError" class="wz-form-error-msg mt-2">{{ requirementError }}</p>
              </div>

              <!-- Resumen verde -->
              <div v-if="requirementSummary" class="p-2 mt-1 rounded" style="background:#d4edda; color:#155724; font-size:0.875rem">
                <i class="ri-checkbox-circle-line mr-1"></i>{{ requirementSummary }}
              </div>
            </div>
          </div>

          <!-- Renovación por período (reingreso) -->
          <div class="w-full mb-3 px-2">
            <wz-checkbox v-model="form.renewsPerPeriod" switch>
              <strong>Se renueva por período de empleo</strong>
            </wz-checkbox>
            <p class="text-muted text-sm mt-1">
              Actívalo para acuses de onboarding (protocolo de hostigamiento, declaración jurada, etc.):
              al reingresar el colaborador, este documento vuelve a pedirse. Déjalo desactivado para
              documentos permanentes (DNI, títulos, antecedentes).
            </p>
          </div>

          <!-- Legajo (CDEF011): quién produce el documento y cuándo aparece -->
          <div class="w-full md:w-1/2 mb-3 px-2">
            <label class="wz-form-label">Origen</label>
            <wz-select v-model="form.origin" :items="originOptions" />
            <p class="text-muted text-sm mt-1">
              Solo los documentos que genera Crystal llevan plantilla y firmantes.
            </p>
          </div>
          <div class="w-full md:w-1/2 mb-3 px-2">
            <label class="wz-form-label">Etapa en que aparece</label>
            <wz-select v-model="form.lifecycleStage" :items="stageOptions" />
          </div>
          <div class="w-full mb-3 px-2">
            <wz-checkbox v-model="form.isConditional" switch>
              <strong>Depende de una condición del colaborador</strong>
            </wz-checkbox>
            <div v-if="form.isConditional" class="mt-2">
              <wz-input
                v-model="form.conditionNote"
                label="Condición (texto para RRHH)"
                placeholder="Ej.: solo si maneja un vehículo de la empresa"
                :error="conditionError"
              />
            </div>
          </div>
          <div class="w-full mb-3 px-2">
            <wz-checkbox v-model="form.perContract" switch>
              <strong>Se emite en cada contrato</strong>
            </wz-checkbox>
            <p class="text-muted text-sm mt-1">
              Desactívalo para acuses de recepción: se entregan una vez por período de empleo en la empresa y no se
              repiten en un contrato nuevo.
            </p>
          </div>
          <div class="w-full mb-3 px-2">
            <wz-checkbox v-model="form.isEmploymentContract" switch>
              <strong>Es el contrato de trabajo o su renovación</strong>
            </wz-checkbox>
            <p class="text-muted text-sm mt-1">
              Solo un documento de un tipo así apaga el aviso de contrato renovado sin documento.
            </p>
          </div>
          <div v-if="form.origin === 'GENERATED'" class="w-full md:w-1/2 mb-3 px-2">
            <label class="wz-form-label">Es el acuse de</label>
            <wz-select v-model="form.acknowledgesTypeId" :items="contentTypeOptions" defaultOption="No es un acuse" />
            <p class="text-muted text-sm mt-1">
              El acuse registra qué versión del documento recibió cada persona, y la puede imprimir con los marcadores
              {{ '${acknowledged.externalCode}' }} y {{ '${acknowledged.externalVersion}' }}.
            </p>
          </div>
          <div v-if="form.origin === 'GENERATED'" class="w-full md:w-1/2 mb-3 px-2">
            <wz-textarea v-model="expectedMarkersText" label="Marcadores que la plantilla debe traer"
                         placeholder="employee.fullName, contract.baseSalary" :rows="2" />
            <p class="text-muted text-sm mt-1">
              Códigos del catálogo, separados por coma. Una plantilla a la que le falte uno se rechaza.
            </p>
          </div>
          <div class="w-full md:w-1/2 mb-3 px-2">
            <wz-checkbox v-model="form.requiresNotarial" switch>
              <strong>Exige firma certificada ante notario</strong>
            </wz-checkbox>
          </div>
          <div class="w-full md:w-1/2 mb-3 px-2">
            <wz-checkbox v-model="form.requiresPhysical" switch>
              <strong>Se mantiene en papel</strong>
            </wz-checkbox>
          </div>
        </div>

        <div class="flex justify-end gap-2 mt-2">
          <wz-button type="button" variant="light" @click="$router.push({ name: 'document-type-list' })">{{ editing ? "Volver" : "Cancelar" }}</wz-button>
          <wz-button type="submit" variant="primary" class="ml-2">Guardar</wz-button>
        </div>
      </wz-card>
    </form>

    <!-- Plantillas y firmantes: solo para tipos ya guardados como GENERATED -->
    <template v-if="editing && savedOrigin === 'GENERATED'">
      <document-templates-section class="mt-4" :document-type-id="Number($route.params.id)" />
      <document-signers-section class="mt-4" :document-type-id="Number($route.params.id)" />
    </template>
  </div>
</template>

<script>
import { Alert } from "@wizepe/wizeui"
import { required } from 'vuelidate/lib/validators'
import { DocumentTypeService, CompanyService, PositionService } from '@/core/services'
import { Constants } from '@/core/utils'
import DocumentTemplatesSection from '../Components/DocumentTemplatesSection.vue'
import DocumentSignersSection from '../Components/DocumentSignersSection.vue'

const emptyLegajo = () => ({
  origin: 'UPLOAD',
  lifecycleStage: 'ONBOARDING',
  isConditional: false,
  conditionNote: null,
  requiresNotarial: false,
  requiresPhysical: false,
  perContract: true,
  isEmploymentContract: false,
  acknowledgesTypeId: null,
})

export default {
  components: { DocumentTemplatesSection, DocumentSignersSection },
  data() {
    return {
      editing: false,
      // Origen guardado: la sección de plantillas aparece cuando el tipo YA es GENERATED en el servidor.
      savedOrigin: null,
      expectedMarkersText: '',
      allTypes: [],
      originOptions: Object.values(Constants.DocumentOrigin).map(o => ({ key: o.key, label: o.label })),
      stageOptions: Object.values(Constants.DocumentLifecycleStage).map(s => ({ key: s.key, label: s.label })),
      form: {
        code: null,
        name: null,
        description: null,
        requirementScope: 'NONE',
        renewsPerPeriod: false,
        ...emptyLegajo(),
      },
      // Cada requisito es un par { companyId, positionId }; null = wildcard.
      requirements: [],
      allCompanies: [],
      allPositions: [],
    }
  },

  validations: { form: { name: { required } } },

  computed: {
    isObligatory: {
      get() { return this.form.requirementScope !== 'NONE' },
      set(val) {
        this.form.requirementScope = val ? 'ALL' : 'NONE'
        if (!val) this.requirements = []
      },
    },

    companyOptions() {
      return this.allCompanies.map(c => ({ key: c.id, label: c.name }))
    },

    positionOptions() {
      return this.allPositions.map(p => ({ key: p.id, label: p.description || p.name }))
    },

    // Filas válidas: al menos uno de los dos lados seleccionado.
    validRequirements() {
      return this.requirements.filter(r => r.companyId || r.positionId)
    },

    contentTypeOptions() {
      return this.allTypes
        .filter(t => t.origin === 'GENERATED' && String(t.id) !== String(this.$route.params.id))
        .map(t => ({ key: t.id, label: t.name }))
    },

    conditionError() {
      const note = this.form.conditionNote || ''
      return note.length > 300 ? 'La condición no debe superar los 300 caracteres' : ''
    },

    requirementError() {
      if (this.form.requirementScope !== 'SPECIFIC') return ''
      if (this.requirements.some(r => !r.companyId && !r.positionId)) {
        return 'Cada requisito debe tener al menos una empresa o un cargo.'
      }
      if (!this.validRequirements.length) {
        return 'Agrega al menos un requisito (empresa y/o cargo).'
      }
      return ''
    },

    requirementSummary() {
      const scope = this.form.requirementScope
      if (scope === 'ALL') return 'Obligatorio para todos los colaboradores'
      if (scope === 'SPECIFIC') {
        if (!this.validRequirements.length) return null
        return this.validRequirements.map(r => {
          const comp = r.companyId ? this.companyName(r.companyId) : 'Cualquier empresa'
          const pos = r.positionId ? this.positionName(r.positionId) : 'Cualquier cargo'
          return `${comp} + ${pos}`
        }).join(' · ')
      }
      return null
    },
  },

  watch: {
    '$route.params.id'() {
      if (this.$route.params.id) this.load(this.$route.params.id)
    },
    'form.requirementScope'(scope) {
      // Al entrar a SPECIFIC sin filas, arrancar con una fila vacía para guiar al usuario.
      if (scope === 'SPECIFIC' && !this.requirements.length) this.addRequirement()
    },
  },

  methods: {
    addRequirement() {
      this.requirements.push({ companyId: null, positionId: null })
    },
    removeRequirement(idx) {
      this.requirements.splice(idx, 1)
    },
    companyName(id) {
      const c = this.allCompanies.find(x => x.id === id)
      return c ? c.name : ''
    },
    positionName(id) {
      const p = this.allPositions.find(x => x.id === id)
      return p ? (p.description || p.name) : ''
    },

    async save() {
      this.$v.$touch()
      if (this.$v.$error) return
      if (this.requirementError) {
        Alert.error(this.requirementError)
        return
      }
      if (this.conditionError) {
        Alert.error(this.conditionError)
        return
      }
      try {
        this.$store.dispatch('app/loading', true)
        const data = {
          name: this.form.name,
          description: this.form.description || null,
          requirementScope: this.form.requirementScope,
          renewsPerPeriod: this.form.renewsPerPeriod,
          requirements: this.form.requirementScope === 'SPECIFIC'
            ? this.validRequirements.map(r => ({ companyId: r.companyId || null, positionId: r.positionId || null }))
            : [],
          origin: this.form.origin,
          lifecycleStage: this.form.lifecycleStage,
          isConditional: this.form.isConditional,
          conditionNote: this.form.isConditional ? (this.form.conditionNote || null) : null,
          requiresNotarial: this.form.requiresNotarial,
          requiresPhysical: this.form.requiresPhysical,
          perContract: this.form.perContract,
          isEmploymentContract: this.form.isEmploymentContract,
          acknowledgesTypeId: this.form.origin === 'GENERATED' ? (this.form.acknowledgesTypeId || null) : null,
          expectedMarkers: this.form.origin === 'GENERATED'
            ? this.expectedMarkersText.split(',').map(m => m.trim()).filter(Boolean)
            : [],
        }
        let response
        if (!this.editing) response = await DocumentTypeService.save(data)
        else response = await DocumentTypeService.update(this.$route.params.id, data)
        this.savedOrigin = response.payload ? response.payload.origin : this.form.origin
        Alert.success('Se almacenó la información correctamente')
        if (!this.editing) {
          this.$router.push({ name: 'document-type-edit', params: { id: response.payload.id } })
          this.editing = true
        }
      } catch (ex) {
        console.error(ex)
        Alert.error(ex.errors ? ex.errors.message : 'Error al guardar')
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },

    async load(id) {
      try {
        this.$store.dispatch('app/loading', true)
        const payload = (await DocumentTypeService.get(id)).payload
        this.form = {
          code: payload.code,
          name: payload.name,
          description: payload.description,
          requirementScope: payload.requirementScope || 'NONE',
          renewsPerPeriod: payload.renewsPerPeriod || false,
          origin: payload.origin || 'UPLOAD',
          lifecycleStage: payload.lifecycleStage || 'ONBOARDING',
          isConditional: payload.isConditional || false,
          conditionNote: payload.conditionNote || null,
          requiresNotarial: payload.requiresNotarial || false,
          requiresPhysical: payload.requiresPhysical || false,
          perContract: payload.perContract !== false,
          isEmploymentContract: payload.isEmploymentContract || false,
          acknowledgesTypeId: payload.acknowledgesTypeId || null,
        }
        this.savedOrigin = payload.origin || 'UPLOAD'
        this.expectedMarkersText = (payload.expectedMarkers || []).join(', ')
        this.requirements = (payload.requirements || []).map(r => ({
          companyId: r.companyId || null,
          positionId: r.positionId || null,
        }))
        this.editing = true
      } catch (e) {
        console.error(e)
        Alert.error(e.errors ? e.errors.message : 'Error al cargar')
        this.$router.push({ name: 'document-type-list' })
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },

    async loadCatalogs() {
      try {
        const [compResp, posResp, typeResp] = await Promise.all([
          CompanyService.listAll(),
          PositionService.listAll(),
          DocumentTypeService.list({ status: 1 }),
        ])
        this.allCompanies = (compResp.payload || [])
        this.allPositions = (posResp.payload || [])
        this.allTypes = (typeResp.payload || [])
      } catch (e) {
        console.error('Error cargando catálogos', e)
      }
    },
  },

  async created() {
    await this.loadCatalogs()
    if (this.$route.params.id) this.load(this.$route.params.id)
  },
}
</script>
